/**
 * ValueInvestor 실시간 계층 — 원본 socket.io 자리를 AWS IoT(MQTT over WebSocket)로 대체한다.
 * ------------------------------------------------------------
 * 원본 서버는 소켓 `BROADCAST` 로 세션 문서 전체를 밀었다. 우리는 **신호(작은 페이로드)** 를 받고
 * 필요한 조각만 REST 로 다시 읽는다(FE-ADOPT-PLAN B2). 이 파일은 연결·구독·재발급만 담당하고,
 * 문서 병합은 `contexts/play-session/provider.tsx` 가 한다.
 *
 * 이식 원본: Vue 프런트 `src/helpers/mqttClient.ts`(같은 백엔드 계약). 함정도 그대로다:
 *   - **서버가 준 clientId 를 그대로 쓴다**(스코프 정책이 정확히 일치해야 연결된다)
 *   - presigned URL 은 만료되므로 만료 5분 전에 스스로 다시 발급받는다
 *   - QoS 1 은 at-least-once 라 `msgId` 로 중복을 걸러낸다
 */
import mqtt, { MqttClient as MqttJsClient } from 'mqtt';
import { HttpClient } from '../apis/http-client';

export interface PresignResponse {
  url: string;
  topics: Record<string, string>;
  publishTopics?: Record<string, string>;
  clientId: string;
  expiresInSec: number;
}

export interface RealtimeHandlers {
  /** 신호 수신 — key 는 presign 이 내려준 토픽 키('state' · 'stocks' · 'team' · 'user' …) */
  onSignal: (topicKey: string, payload: unknown) => void;
  /** 연결이 실제로 성립한 뒤(재연결 포함) — 놓친 변화를 다시 읽는 자리 */
  onConnected?: () => void;
}

const RENEW_BEFORE_MS = 5 * 60 * 1000;
const SEEN_LIMIT = 200;

/** 호환 API `GET /apis/c/{clientId}/game/mqtt-presign` — HttpClient 의 baseURL 이 이미 고객사 경로다 */
async function presign(sessionId: string): Promise<PresignResponse | null> {
  try {
    const res = await HttpClient.get('/game/mqtt-presign', { params: { sessionId } });
    return (res.data as PresignResponse) ?? null;
  } catch (e) {
    console.error('[mqtt] presign 실패 — sessionId =', sessionId, e);
    return null;
  }
}

export class LegacyRealtime {
  private client: MqttJsClient | null = null;
  private topics: Record<string, string> = {};
  private publishTopics: Record<string, string> = {};
  private handlers: RealtimeHandlers | null = null;
  private sessionId: string | null = null;
  private renewTimer: ReturnType<typeof setTimeout> | null = null;
  private seen = new Set<string>();
  private seenOrder: string[] = [];
  private generation = 0;
  private reconnects = 0;

  async connect(sessionId: string, handlers: RealtimeHandlers): Promise<boolean> {
    this.handlers = handlers;
    this.sessionId = sessionId;
    return this.presignAndConnect();
  }

  private async presignAndConnect(): Promise<boolean> {
    if (this.sessionId === null) return false;
    const gen = ++this.generation;
    const data = await presign(this.sessionId);
    if (!data || !data.url) return false;
    if (gen !== this.generation) return false;
    return this.connectWith(data, gen);
  }

  private connectWith(data: PresignResponse, gen: number): boolean {
    this.closeClient();
    this.topics = data.topics || {};
    this.publishTopics = data.publishTopics || this.topics;

    this.client = mqtt.connect(data.url, {
      clientId: data.clientId,
      clean: true,
      protocolVersion: 4,
      keepalive: 30,
      reconnectPeriod: 3000,
      connectTimeout: 10000,
    });

    this.client.on('connect', () => {
      if (gen !== this.generation) return;
      const list = Object.values(this.topics);
      if (!list.length) {
        this.handlers?.onConnected?.();
        return;
      }
      this.client?.subscribe(list, { qos: 1 }, (err) => {
        if (err) {
          console.error('[mqtt] subscribe 실패 —', list, err);
          return;
        }
        this.handlers?.onConnected?.();
      });
    });
    this.client.on('message', (topic, buf) => {
      if (gen !== this.generation) return;
      this.onMessage(topic, buf);
    });
    this.client.on('reconnect', () => {
      if (gen !== this.generation) return;
      this.reconnects += 1;
      if (this.reconnects === 1 || this.reconnects % 10 === 0) {
        console.error(`[mqtt] 재연결 시도 ${this.reconnects}회 — 실시간이 불안정하다`);
      }
    });
    this.client.on('error', (e) => {
      console.error('[mqtt] 연결 오류 —', e);
    });

    this.scheduleRenew(data.expiresInSec, gen);
    return true;
  }

  private scheduleRenew(expiresInSec: number, gen: number) {
    this.clearRenew();
    if (!expiresInSec || expiresInSec <= 0) return;
    const delay = Math.max(expiresInSec * 1000 - RENEW_BEFORE_MS, 30_000);
    this.renewTimer = setTimeout(() => {
      if (gen !== this.generation) return;
      void this.presignAndConnect();
    }, delay);
  }

  private onMessage(topic: string, buf: Uint8Array) {
    let payload: unknown;
    if (buf?.length) {
      try {
        payload = JSON.parse(new TextDecoder().decode(buf));
      } catch (e) {
        console.error('[mqtt] 페이로드 파싱 실패 — topic =', topic, e);
      }
    }
    const msgId = (payload as { msgId?: unknown } | undefined)?.msgId;
    if (typeof msgId === 'string') {
      if (this.seen.has(msgId)) return;
      this.seen.add(msgId);
      this.seenOrder.push(msgId);
      if (this.seenOrder.length > SEEN_LIMIT) {
        const old = this.seenOrder.shift();
        if (old) this.seen.delete(old);
      }
    }
    const key = Object.keys(this.topics).find((k) => this.topics[k] === topic) ?? topic;
    this.handlers?.onSignal(key, payload);
  }

  /** 쓰기 API 가 성공한 뒤 서버가 돌려준 신호를 그대로 발행한다(팀원·현황판 갱신용) */
  publish(topicKey: string, payload: Record<string, unknown> = {}) {
    const topic = this.publishTopics[topicKey] || this.topics[topicKey];
    if (!this.client || !this.client.connected || !topic) {
      console.error('[mqtt] publish 불가 — key =', topicKey, ', connected =', !!this.client?.connected);
      return;
    }
    const body = { msgId: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`, ...payload };
    try {
      this.client.publish(topic, JSON.stringify(body), { qos: 1 }, (err) => {
        if (err) console.error('[mqtt] publish 실패 — key =', topicKey, err);
      });
    } catch (e) {
      console.error('[mqtt] publish 예외 — key =', topicKey, e);
    }
  }

  private clearRenew() {
    if (this.renewTimer) {
      clearTimeout(this.renewTimer);
      this.renewTimer = null;
    }
  }

  private closeClient() {
    if (!this.client) return;
    try {
      this.client.end(true);
    } catch (e) {
      console.error('[mqtt] 연결 종료 실패', e);
    }
    this.client = null;
  }

  end() {
    this.generation++;
    this.clearRenew();
    this.closeClient();
    this.handlers = null;
    this.sessionId = null;
    this.seen.clear();
    this.seenOrder = [];
    this.publishTopics = {};
    this.reconnects = 0;
  }
}
